import type {FC} from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import {toPlainText} from 'next-sanity';

import {buttonClasses} from '@/components/button';
import {Media} from '@/components/media';
import type {Article365} from '@/schema/documents/article-365';
import {documentToHref, formatDate} from '@/utils';

interface Props {
	article: Article365;
}

export const Item: FC<Props> = ({article}) => {
	const {media, publishDate, content, link, heading} = article;
	let href: string | null = null;

	if (link?.type === 'internal') {
		href = link.reference
			? documentToHref(
					link.reference.category?.slug?.current,
					link.reference.slug?.current,
					link.reference._type,
				)
			: null;
	} else if (link?.type === 'external') {
		href = link?.href ?? null;
	}

	return (
		<div
			className={clsx(
				articleListItemClasses(),
				'gap-y-4',
				'grid-cols-1',
				'grid-rows-[auto_auto_1fr_auto]',
				'relative',
			)}
		>
			<div
				className={clsx(
					'w-full',
					'h-full',
					'flex',
					'flex-col',
					'justify-center',
					'overflow-hidden',
					'relative',
					'rounded-[5px]',
					'aspect-square',
				)}
			>
				<Media
					media={media}
					disabledControls={true}
					disabledMask={true}
				/>
			</div>
			{heading ? (
				<h2 className={clsx('text-sm', 'font-bold')}>{heading}</h2>
			) : (
				<h2 className={clsx('text-sm', 'font-bold')}>
					{formatDate(publishDate)}
				</h2>
			)}
			<p
				className={clsx(
					'font-medium',
					'max-sm:text-xs',
					'text-bodyMedium',
					'line-clamp-4',
				)}
			>
				{content ? toPlainText(content) : null}
			</p>

			{href ? (
				<Link
					href={href}
					className={clsx(
						'ring-disabled',
						'group',
						'after:absolute',
						'after:inset-0',
						'after:rounded-[15px]',
						'focus-visible:after:ring-focus',
						'active:after:shadow-focus',
					)}
					{...(link?.targetBlank
						? {target: '_blank', rel: 'noopener noreferrer'}
						: {})}
				>
					<ItemButton text={link?.overrideText ?? 'Se mer'} />
				</Link>
			) : null}
		</div>
	);
};

const ItemButton: FC<{text?: string}> = ({text}) => {
	return (
		<div
			className={clsx(
				buttonClasses,
				'mt-2',
				'group-active:shadow-hover',
				'group-hover:rounded-lg',
			)}
		>
			{text}
		</div>
	);
};

const articleListItemClasses = () =>
	clsx(
		'h-full',
		'w-full',
		'grid',
		'relative',
		'rounded-[15px]',
		'p-8',

		'justify-start',
		'items-start',

		'group-focus-visible:ring-focus',
		'group-active:shadow-focus',
		'bg-[#FAF0D9]',
	);
